import React, { useEffect, useState, useCallback, useRef } from "react";
import styled from "@emotion/styled";
import { useSelector, useDispatch } from "react-redux";
import { RootState, AppDispatch } from "@store/index";
import { Module, Dispatch as DispatchApi } from "@apis/index";
import { useSearchParams } from "react-router-dom";
import useConfig from "@hooks/useConfig";
import {
  GoogleMap,
  Marker,
  InfoWindow,
  useJsApiLoader,
} from "@react-google-maps/api";
import {
  Search,
  MapPin,
  Phone,
  Mail,
  Navigation,
  FileText,
} from "lucide-react";

const MAP_LIBRARIES: any = ["places", "geometry"];

const DispatchDashboard = () => {
  const dispatch = useDispatch<AppDispatch>();
  const [searchParams, setSearchParams] = useSearchParams();
  const { config } = useConfig();
  const { data: modules } = useSelector((state: RootState) => state.modules);

  const selectedModuleId = Number(searchParams.get("moduleId")) || undefined;

  const [stats, setStats] = useState<any>(null);
  const [loading, setLoading] = useState(false);
  const [activeDeliveryMen, setActiveDeliveryMen] = useState<any[]>([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedMarker, setSelectedMarker] = useState<any | null>(null);

  const { isLoaded } = useJsApiLoader({
    googleMapsApiKey: process.env.APP_MAP_API_KEY || "",
    libraries: MAP_LIBRARIES,
  });

  const mapRef = useRef<google.maps.Map | null>(null);

  useEffect(() => {
    const fetchModules = async () => {
      if (!modules || modules.length === 0) {
        await Module.list({ state: "modules" });
      }
    };
    fetchModules();
  }, [modules, dispatch]);

  useEffect(() => {
    if (modules && modules.length > 0) {
      if (!selectedModuleId) {
        const savedModuleId = localStorage.getItem("selectedModuleId");
        let targetModuleId = String(modules[0].id);
        if (savedModuleId) {
          const found = modules.find(
            (mod: any) => String(mod.id) === savedModuleId,
          );
          if (found) {
            targetModuleId = savedModuleId;
          }
        }
        setSearchParams({ moduleId: targetModuleId }, { replace: true });
      } else {
        const matchedModule = modules.find(
          (mod: any) => mod.id === selectedModuleId,
        );
        if (matchedModule) {
          localStorage.setItem("selectedModuleId", String(selectedModuleId));
          localStorage.setItem("selectedModule", matchedModule.name);
        }
      }
    }
  }, [modules, selectedModuleId, setSearchParams]);

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      const [overviewRes, mapRes]: any = await Promise.all([
        DispatchApi.getOverview({ params: { moduleId: selectedModuleId } }),
        DispatchApi.getActiveDeliveryMen({
          params: { moduleId: selectedModuleId, search: searchQuery },
        }),
      ]);

      setStats(overviewRes);
      setActiveDeliveryMen(mapRes || []);

      // Center map loosely around first result if bounds are not set
      if (mapRef.current && mapRes && mapRes.length > 0) {
        const bounds = new window.google.maps.LatLngBounds();
        mapRes.forEach((point: any) => {
          if (point.currentLatitude && point.currentLongitude) {
            bounds.extend({
              lat: parseFloat(point.currentLatitude),
              lng: parseFloat(point.currentLongitude),
            });
          }
        });
        mapRef.current.fitBounds(bounds);

        // Prevent excessive zoom level if there's only one marker or points are very close
        const listener = window.google.maps.event.addListener(
          mapRef.current,
          "idle",
          () => {
            if (mapRef.current && mapRef.current.getZoom()! > 16) {
              mapRef.current.setZoom(16);
            }
            window.google.maps.event.removeListener(listener);
          },
        );
      }
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, [selectedModuleId, searchQuery]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleSearchKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === "Enter") {
      fetchData();
    }
  };

  const statusConfig = [
    {
      title: "Active Delivery Man",
      key: "active",
      count: stats?.deliveryMen?.active || 0,
      icon: "🛵",
      color: "#e0f2fe",
      textColor: "#0369a1",
    },
    {
      title: "In Active",
      key: "inActive",
      count: stats?.deliveryMen?.inActive || 0,
      icon: "💤",
      color: "#f1f5f9",
      textColor: "#475569",
    },
    {
      title: "Suspended",
      key: "suspended",
      count: stats?.deliveryMen?.suspended || 0,
      icon: "⛔",
      color: "#fee2e2",
      textColor: "#b91c1c",
    },
    {
      title: "Fully Booked Delivery Man",
      key: "fullyBooked",
      count: stats?.deliveryMen?.fullyBooked || 0,
      icon: "🎒",
      color: "#ffedd5",
      textColor: "#c2410c",
    },
    {
      title: "Available To Assign More Order",
      key: "available",
      count: stats?.deliveryMen?.available || 0,
      icon: "✅",
      color: "#dcfce7",
      textColor: "#15803d",
    },
    {
      title: "Unassigned Orders",
      key: "unassigned",
      count: stats?.orders?.unassigned || 0,
      icon: "📋",
      color: "#fef3c7",
      textColor: "#b45309",
    },
    {
      title: "Accepted By Delivery Man",
      key: "accepted",
      count: stats?.orders?.accepted || 0,
      icon: "👍",
      color: "#ede9fe",
      textColor: "#6d28d9",
    },
    {
      title: "Out For Delivery",
      key: "outForDelivery",
      count: stats?.orders?.outForDelivery || 0,
      icon: "🚚",
      color: "#e0e7ff",
      textColor: "#4338ca",
    },
  ];

  return (
    <React.Fragment>
      <PageContainer>
        <Header>
          <div className="title-area">
            <div>
              <h2 className="title">Dispatch Overview</h2>
              <p className="subtitle">
                Monitor your Dispatch Management Statistics
              </p>
            </div>
          </div>
          <div className="filter-area"></div>
        </Header>

        <StatsGrid>
          {statusConfig.map((item, idx) => (
            <StatCard key={idx} bgColor={item.color}>
              <div className="icon">
                <span style={{ fontSize: "24px" }}>{item.icon}</span>
              </div>
              <div className="content">
                <div className="count" style={{ color: item.textColor }}>
                  {item.count}
                </div>
                <div className="title">{item.title}</div>
              </div>
            </StatCard>
          ))}
        </StatsGrid>

        <MapSection>
          <div className="map-header">
            <h3>Currently Active Delivery Men</h3>
            <SearchWrapper>
              <div className="search-input-wrapper">
                <Search size={18} className="search-icon" />
                <input
                  type="text"
                  placeholder="Search Delivery Man..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  onKeyPress={handleSearchKeyPress}
                />
              </div>
              <button className="search-btn" onClick={() => fetchData()}>
                Search
              </button>
            </SearchWrapper>
            <a
              href="/admin/users/delivery-man/delivery-man-lists"
              className="view-all"
            >
              View All Delivery Men
            </a>
          </div>

          <MapContainer>
            {isLoaded ? (
              <GoogleMap
                mapContainerStyle={{ width: "100%", height: "100%" }}
                zoom={12}
                center={{ lat: 27.7172, lng: 85.324 }}
                onLoad={(map) => {
                  mapRef.current = map;
                }}
                options={{
                  disableDefaultUI: false,
                  mapTypeControl: true,
                }}
              >
                {activeDeliveryMen.map((man: any) => {
                  if (!man.currentLatitude || !man.currentLongitude)
                    return null;
                  return (
                    <Marker
                      key={man.id}
                      position={{
                        lat: parseFloat(man.currentLatitude),
                        lng: parseFloat(man.currentLongitude),
                      }}
                      icon={{
                        url: "/svgs/delivery-pin.svg", // Fallback to standard if we don't have custom pin
                        scaledSize: new window.google.maps.Size(40, 40),
                      }}
                      onClick={() => setSelectedMarker(man)}
                    />
                  );
                })}
                {selectedMarker && (
                  <InfoWindow
                    position={{
                      lat: parseFloat(selectedMarker.currentLatitude),
                      lng: parseFloat(selectedMarker.currentLongitude),
                    }}
                    onCloseClick={() => setSelectedMarker(null)}
                  >
                    <InfoCard>
                      <div className="user-header">
                        <div className="avatar">
                          {selectedMarker.avatar ? (
                            <img
                              src={
                                config?.bucketUrl
                                  ? `${config.bucketUrl}/${selectedMarker.avatar}`
                                  : selectedMarker.avatar
                              }
                              alt=""
                            />
                          ) : (
                            "👨🏻‍🚀"
                          )}
                        </div>
                        <div className="names">
                          <h4>{selectedMarker.name}</h4>
                          <span>ID: {selectedMarker.id}</span>
                        </div>
                      </div>
                      <div className="user-details">
                        <div className="detail-item">
                          <Phone size={14} /> {selectedMarker.phoneNo}
                        </div>
                        <a
                          href={`/admin/users/delivery-man/delivery-man-lists/view/${selectedMarker.id}`}
                          className="view-profile"
                        >
                          View Profile
                        </a>
                      </div>
                    </InfoCard>
                  </InfoWindow>
                )}
              </GoogleMap>
            ) : (
              <div className="loading">Loading Map...</div>
            )}
          </MapContainer>
        </MapSection>
      </PageContainer>
    </React.Fragment>
  );
};

export default DispatchDashboard;

const PageContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 24px;
  width: 100%;
`;

const Header = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  background: white;
  border-radius: 12px;
  padding: 24px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.05);

  .title-area {
    display: flex;
    align-items: center;
    gap: 16px;

    .title {
      font-size: 20px;
      font-weight: 700;
      color: #0f172a;
      margin: 0 0 4px 0;
    }

    .subtitle {
      font-size: 14px;
      color: #64748b;
      margin: 0;
    }
  }

  .filter-area {
    display: flex;
    align-items: center;
    gap: 12px;

    .label {
      font-size: 14px;
      font-weight: 600;
      color: #334155;
    }
  }
`;

const Select = styled.select`
  padding: 8px 16px;
  border: 1px solid #cbd5e1;
  border-radius: 8px;
  font-size: 14px;
  color: #0f172a;
  background-color: white;
  min-width: 180px;
  outline: none;
  cursor: pointer;

  &:focus {
    border-color: #3b82f6;
  }
`;

const StatsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(280px, 1fr));
  gap: 16px;
`;

const StatCard = styled.div<{ bgColor: string }>`
  background: white;
  border-radius: 12px;
  padding: 20px;
  display: flex;
  align-items: center;
  gap: 16px;
  border: 1px solid #f1f5f9;
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.03);
  transition:
    transform 0.2s,
    box-shadow 0.2s;

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1);
  }

  .icon {
    width: 60px;
    height: 60px;
    border-radius: 12px;
    display: flex;
    align-items: center;
    justify-content: center;
    background: ${(props) => props.bgColor};
  }

  .content {
    display: flex;
    flex-direction: column;
    gap: 4px;

    .count {
      font-size: 28px;
      font-weight: 800;
      line-height: 1;
    }

    .title {
      font-size: 13px;
      font-weight: 600;
      color: #64748b;
    }
  }
`;

const MapSection = styled.div`
  background: white;
  border-radius: 12px;
  border: 1px solid #e2e8f0;
  overflow: hidden;
  display: flex;
  flex-direction: column;

  .map-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 16px 24px;
    border-bottom: 1px solid #e2e8f0;

    h3 {
      margin: 0;
      font-size: 16px;
      font-weight: 600;
      color: #0f172a;
    }

    .view-all {
      font-size: 13px;
      color: #3b82f6;
      font-weight: 600;
      text-decoration: none;

      &:hover {
        text-decoration: underline;
      }
    }
  }
`;

const SearchWrapper = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;

  .search-input-wrapper {
    position: relative;
    display: flex;
    align-items: center;

    .search-icon {
      position: absolute;
      left: 12px;
      color: #94a3b8;
    }

    input {
      padding: 8px 16px 8px 36px;
      border: 1px solid #cbd5e1;
      border-radius: 6px;
      font-size: 13px;
      min-width: 250px;
      outline: none;

      &:focus {
        border-color: #3b82f6;
      }
    }
  }

  .search-btn {
    padding: 8px 16px;
    background: #3b82f6;
    color: white;
    border: none;
    border-radius: 6px;
    font-size: 13px;
    font-weight: 600;
    cursor: pointer;

    &:hover {
      background: #2563eb;
    }
  }
`;

const MapContainer = styled.div`
  width: 100%;
  height: 600px;
  position: relative;

  .loading {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 100%;
    height: 100%;
    background: #f8fafc;
    color: #64748b;
    font-weight: 500;
  }
`;

const InfoCard = styled.div`
  padding: 4px;
  min-width: 160px;

  .user-header {
    display: flex;
    align-items: center;
    gap: 12px;
    margin-bottom: 12px;

    .avatar {
      width: 40px;
      height: 40px;
      border-radius: 50%;
      background: #f1f5f9;
      display: flex;
      align-items: center;
      justify-content: center;
      font-size: 20px;
      overflow: hidden;

      img {
        width: 100%;
        height: 100%;
        object-fit: cover;
      }
    }

    .names {
      h4 {
        margin: 0 0 2px 0;
        font-size: 14px;
        color: #0f172a;
      }
      span {
        font-size: 11px;
        color: #64748b;
        font-weight: 600;
      }
    }
  }

  .user-details {
    display: flex;
    flex-direction: column;
    gap: 8px;

    .detail-item {
      display: flex;
      align-items: center;
      gap: 8px;
      font-size: 13px;
      color: #334155;
    }

    .view-profile {
      margin-top: 8px;
      display: block;
      text-align: center;
      padding: 6px;
      background: #f1f5f9;
      color: #3b82f6;
      font-size: 12px;
      font-weight: 600;
      border-radius: 4px;
      text-decoration: none;

      &:hover {
        background: #e2e8f0;
      }
    }
  }
`;
